import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { Twitter, Instagram, Linkedin, Youtube } from "lucide-react";
import { BUSINESS } from "@/lib/business";

/**
 * Shared marketing footer used by the landing page's legal pages
 * (privacy / terms). Self-contained bilingual copy — does NOT depend
 * on translations.js because footer strings are page-local in the
 * LandingPage's own COPY object.
 */

const COPY = {
  ar: {
    tagline: "نظام التشغيل الذكي للنمو الرقمي — للأعمال، المبدعين، والوكالات في السوق السعودي والخليجي.",
    product: "المنتج",
    productLinks: ["الميزات", "الخطط", "قصص النجاح", "الشركاء"],
    company: "الشركة",
    companyLinks: ["من نحن", "الفريق", "الوظائف", "الأخبار"],
    legal: "قانوني",
    contact: "تواصل معنا",
    rights: "جميع الحقوق محفوظة",
    madeIn: "صُنع بحب في الرياض",
    email: BUSINESS.email_support,
    phone: BUSINESS.phone_display || "+٩٦٦ ٥٥ ٨٣٧ ١٣٧١",
    phoneHref: BUSINESS.phone_href || "+966558371371",
    legalLinks: [
      { label: "شروط الاستخدام", to: "/terms" },
      { label: "سياسة الخصوصية", to: "/privacy" },
      { label: "سياسة الاسترداد", to: "/refund" },
      { label: "إلغاء الاشتراك", to: "/cancellation" },
      { label: "الدعم", to: "/support" },
    ],
  },
  en: {
    tagline: "The intelligent digital-growth operating system — for businesses, creators and agencies in Saudi Arabia and the GCC.",
    product: "Product",
    productLinks: ["Features", "Pricing", "Success stories", "Partners"],
    company: "Company",
    companyLinks: ["About", "Team", "Careers", "News"],
    legal: "Legal",
    contact: "Contact",
    rights: "All rights reserved",
    madeIn: "Made with love in Riyadh",
    email: BUSINESS.email_support,
    phone: BUSINESS.phone_display || "+966 55 837 1371",
    phoneHref: BUSINESS.phone_href || "+966558371371",
    legalLinks: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Refund Policy", to: "/refund" },
      { label: "Cancellation", to: "/cancellation" },
      { label: "Support", to: "/support" },
    ],
  },
};

export default function MarketingFooter() {
  const { lang } = useI18n();
  const f = COPY[lang] || COPY.en;

  return (
    <footer data-testid="marketing-footer" className="bg-primary text-primary-foreground pt-16 md:pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 font-display font-semibold text-2xl">
              <img
                src="/brand/daam-mark-gold-128.png?v=2"
                alt="DAAM"
                width={28}
                height={28}
                style={{ width: 28, height: 28, objectFit: "contain" }}
                data-testid="marketing-footer-logo-mark"
              />
              <span>DAAM</span>
            </Link>
            <p className="mt-4 text-sm text-white/60 max-w-xs leading-relaxed">{f.tagline}</p>
            <div className="mt-6 flex items-center gap-3">
              {[Twitter, Instagram, Linkedin, Youtube].map((I, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`marketing-footer-social-${i}`}
                  className="w-9 h-9 rounded-sm border border-white/20 flex items-center justify-center hover:bg-[#B8875D] hover:border-[#B8875D] transition-colors"
                >
                  <I className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{f.product}</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {f.productLinks.map((l, i) => (
                <li key={i}><Link to="/#features" className="hover:text-white transition">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{f.company}</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {f.companyLinks.map((l, i) => (
                <li key={i}><a href="#" className="hover:text-white transition">{l}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{f.legal}</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {f.legalLinks.map((l, i) => (
                <li key={i}>
                  <Link
                    to={l.to}
                    data-testid={`marketing-footer-legal-${i}`}
                    className="hover:text-white transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4B08A] mb-4">{f.contact}</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li>
                <a
                  href={`mailto:${f.email}`}
                  data-testid="marketing-footer-email-link"
                  className="hover:text-white transition break-all"
                >
                  {f.email}
                </a>
              </li>
              <li className="tabular-nums" dir="ltr">
                <a
                  href={`tel:${f.phoneHref}`}
                  data-testid="marketing-footer-phone-link"
                  className="hover:text-white transition"
                >
                  {f.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} DAAM. {f.rights}</div>
          <div className="flex items-center gap-2">
            <span>{f.madeIn}</span>
            <span className="text-[#B8875D]">♥</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
